/**
 * Search Insights — Stage 4.
 *
 * Sources `/api/bi/search/insights/` and `/api/bi/search/zero-results/`,
 * powered by the per-event `SearchHistoryLog` table on the backend.
 */
import { useMemo, useState } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Search, AlertTriangle } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { DataView } from '@/components/data/DataView';
import { Sparkline } from '@/components/charts/Sparkline';
import { KPICard } from '@/components/charts/KPICard';
import {
  useSearchInsights,
  useSearchZeroResults,
  type SearchInsightsRow,
  type SearchZeroResultsRow,
} from '@/api/bi';
import { rangeISO, useFilters } from '@/state/filters';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

export function SearchInsightsPage() {
  const range = useFilters((s) => s.range);
  const storeIds = useFilters((s) => s.storeIds);
  const timeRangeMode = useFilters((s) => s.timeRangeMode);
  const [tab, setTab] = useState<'top' | 'zero'>('top');

  const { start: startISO, end: endISO } = useMemo(
    () => rangeISO(range, timeRangeMode),
    [range, timeRangeMode],
  );

  const insights = useSearchInsights({ start: startISO, end: endISO, storeIds, limit: 50 });
  const zero = useSearchZeroResults({ start: startISO, end: endISO, storeIds, limit: 100 });

  const totals = insights.data?.totals;
  const trend = insights.data?.trend ?? [];
  const trendSeries = trend.map((b) => ({ value: b.count }));
  const zeroSeries = trend.map((b) => ({ value: b.zero_results }));

  const insightsCols = useMemo<ColumnDef<SearchInsightsRow, unknown>[]>(
    () => [
      {
        id: 'term',
        header: 'Query',
        cell: ({ row }) => (
          <div className="font-mono text-sm">{row.original.term || '—'}</div>
        ),
      },
      {
        id: 'count',
        header: () => <div className="text-right">Searches</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.number(row.original.count)}
          </div>
        ),
      },
      {
        id: 'unique',
        header: () => <div className="text-right">People</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.unique_users)}</div>
        ),
      },
      {
        id: 'zero',
        header: () => <div className="text-right">No-result</div>,
        cell: ({ row }) => {
          const rate = row.original.no_result_rate;
          const tone = rate >= 0.5 ? 'danger' : rate >= 0.2 ? 'warning' : 'success';
          return (
            <div className="flex items-center justify-end gap-2">
              <span className="kpi-num text-sm">{fmt.number(row.original.zero_results)}</span>
              <Badge tone={tone}>{fmt.pct(rate)}</Badge>
            </div>
          );
        },
      },
    ],
    [],
  );

  const zeroCols = useMemo<ColumnDef<SearchZeroResultsRow, unknown>[]>(
    () => [
      {
        id: 'term',
        header: 'Missing query',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-warning" aria-hidden />
            <span className="font-mono text-sm">{row.original.term || '—'}</span>
          </div>
        ),
      },
      {
        id: 'count',
        header: () => <div className="text-right">Misses</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm font-medium">
            {fmt.number(row.original.count)}
          </div>
        ),
      },
      {
        id: 'unique',
        header: () => <div className="text-right">People</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-sm">{fmt.number(row.original.unique_users)}</div>
        ),
      },
      {
        id: 'last',
        header: 'Last seen',
        cell: ({ row }) =>
          row.original.last_seen ? (
            <span className="text-xs text-muted-foreground">
              {format(new Date(row.original.last_seen), 'MMM d, HH:mm')}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground/60">—</span>
          ),
      },
    ],
    [],
  );

  return (
    <>
      <PageHeader
        eyebrow="Merchandising"
        title="Search Insights"
        description="What shoppers are typing, what we hand back, and where the catalogue is letting them down."
      />

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KPICard
          label="Searches"
          value={fmt.number(totals?.events ?? 0)}
          loading={insights.isLoading}
          trend={trendSeries}
          hint="Search events in window."
        />
        <KPICard
          label="No-result rate"
          value={fmt.pct(totals?.no_result_rate ?? 0)}
          goodWhenUp={false}
          loading={insights.isLoading}
          trend={zeroSeries}
          hint="Share of searches that returned zero matches."
        />
        <KPICard
          label="Curious shoppers"
          value={fmt.number(totals?.unique_users ?? 0)}
          loading={insights.isLoading}
          hint="Distinct users who searched at least once."
        />
        <KPICard
          label="Zero-result hits"
          value={fmt.number(totals?.zero_results ?? 0)}
          goodWhenUp={false}
          loading={insights.isLoading}
          trend={zeroSeries}
          hint="Total searches that came back empty-handed."
        />
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Search className="h-4 w-4" /> Daily search volume
            </CardTitle>
            {trend.length > 0 ? (
              <span className="text-[11px] text-muted-foreground">
                {trend.length} day{trend.length === 1 ? '' : 's'}
              </span>
            ) : null}
          </CardHeader>
          <CardBody>
            {insights.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : trend.length === 0 ? (
              <EmptyState title="No searches yet" description="Pick a wider window or a different store." />
            ) : (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    All searches
                  </div>
                  <Sparkline data={trendSeries} tone="primary" />
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                    Zero results
                  </div>
                  <Sparkline data={zeroSeries} tone="warning" />
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Headline</CardTitle>
            <Badge tone="neutral">live from /api/bi/search/insights/</Badge>
          </CardHeader>
          <CardBody className="space-y-2 text-sm">
            <p className="text-muted-foreground">
              Use this to spot demand we are not satisfying — anything trending in the no-result list
              is either missing in the catalogue, mis-tagged, or out of stock everywhere.
            </p>
            <ul className="space-y-1 text-xs">
              <li>• Sort table by <strong>No-result %</strong> to find merchandising gaps.</li>
              <li>• Cross-check the worst offenders against <strong>Wishlist demand</strong>.</li>
              <li>• Window respects the global date + store filter.</li>
            </ul>
          </CardBody>
        </Card>
      </div>

      <div className="mt-4">
        <div className="mb-2 inline-flex items-center gap-1 rounded-full border border-border bg-surface p-0.5 text-[11px]">
          {(
            [
              { id: 'top', label: 'Top searches' },
              { id: 'zero', label: 'Zero results' },
            ] as const
          ).map((t) => (
            <motion.button
              key={t.id}
              type="button"
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 320, damping: 20 }}
              onClick={() => setTab(t.id)}
              className={cn(
                'press rounded-full px-3 py-1 focus-ring transition-colors',
                tab === t.id ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {t.label}
            </motion.button>
          ))}
        </div>

        {tab === 'top' ? (
          insights.isError ? (
            <ErrorState description="Couldn't load search insights." onRetry={() => void insights.refetch()} />
          ) : (
            <DataView<SearchInsightsRow>
              title="Most searched terms"
              data={insights.data?.rows ?? []}
              columns={insightsCols}
              isLoading={insights.isLoading}
              emptyTitle="No searches in this window"
            />
          )
        ) : zero.isError ? (
          <ErrorState description="Couldn't load zero-result terms." onRetry={() => void zero.refetch()} />
        ) : (
          <DataView<SearchZeroResultsRow>
            title="Things shoppers asked for that we don't carry"
            data={zero.data?.rows ?? []}
            columns={zeroCols}
            isLoading={zero.isLoading}
            emptyTitle="Hooray — nothing came up empty"
          />
        )}
      </div>
    </>
  );
}
